import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAlert } from "../../context/AlertContext";
import AuthService from "../../api/AuthService";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import dayjs from "dayjs";
import ThresholdPage from "../../entities/ThresholdPage";

const Threshold = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [taxPeriod, setTaxPeriod] = useState([]);
  const [year, setYear] = useState("");
  const location = useLocation();
  const { taxCountry } = location.state || {};
  const { showAlert } = useAlert();

  useEffect(() => {
    getTaxPeriod();
  }, []);

  const getTaxPeriod = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getTaxPeriod(taxCountry);
      if (result?.success) {
        setTaxPeriod(result?.data);
        const currentYear = new Date().getFullYear();
        const matchedYear = result?.data?.find((item) => {
          const startYear = dayjs(item?.start).year();
          return startYear === currentYear;
        });
        const selectedYear = matchedYear || result?.data?.[0];
        const selectedYearId = selectedYear?._id || null;
        setYear(selectedYearId);
        if (selectedYear) {
          await getThresholds(taxCountry, selectedYear.start, selectedYear.end);
        }
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const resolveYearDates = (yearId) => {
    const selectedPeriod = taxPeriod?.find((item) => item._id === yearId);
    return {
      start: selectedPeriod?.start || null,
      end: selectedPeriod?.end || null,
    };
  };

  const getThresholds = async (country, start, end) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getRebateThresholds({ country, start, end });
      if (result?.success) {
        setData(result?.data?.rebateThresholds || []);
      } else {
        showAlert(result?.message, "error");
        setData([]);
      }
    } catch (error) {
      setData([]);
    } finally {
      LoaderHelper.loaderStatus(false);
      setLoading(false);
    }
  };

  const getYearThresholds = async (yearId) => {
    const { start, end } = resolveYearDates(yearId);
    setYear(yearId);
    await getThresholds(taxCountry, start, end);
  };

  const refreshThresholds = async () => {
    const { start, end } = resolveYearDates(year);
    await getThresholds(taxCountry, start, end);
  };

  const handleAddThreshold = async (ageFrom, ageTo, thresholdAmount, country, yearId) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const { start, end } = resolveYearDates(yearId);
      const result = await AuthService.createRebateThreshold({
        ageFrom,
        ageTo,
        country: country || taxCountry,
        thresholdValue: thresholdAmount,
        start,
        end,
      });
      if (result?.success) {
        showAlert(result?.message, "success");
        await refreshThresholds();
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message, "error");
    } finally {
      LoaderHelper.loaderStatus(false);
      setLoading(false);
    }
  };

  const handleEditThreshold = async (thresholdId, ageFrom, ageTo, thresholdAmount, yearId) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const { start, end } = resolveYearDates(yearId);
      const result = await AuthService.updateRebateThreshold(thresholdId, {
        ageFrom,
        ageTo,
        country: taxCountry,
        thresholdValue: thresholdAmount,
        start,
        end,
      });
      if (result?.success) {
        showAlert(result?.message, "success");
        await refreshThresholds();
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message, "error");
    } finally {
      LoaderHelper.loaderStatus(false);
      setLoading(false);
    }
  };

  const handleDeleteThreshold = async (thresholdId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteRebateThreshold(thresholdId);
      if (result?.success) {
        showAlert(result?.message, "success");
        await refreshThresholds();
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message, "error");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <ThresholdPage
      data={data}
      taxCountry={taxCountry}
      taxPeriod={taxPeriod}
      year={year}
      loading={loading}
      handleAddThreshold={handleAddThreshold}
      handleEditThreshold={handleEditThreshold}
      handleDeleteThreshold={handleDeleteThreshold}
      getYearThresholds={getYearThresholds}
    />
  );
};

export default Threshold;
